/**
 * WMS1022_R01(입고등록) 라벨 데이터 빌더
 *
 * 흐름 1) 저장 시 자동 출력
 * - 사용자가 저장 버튼 클릭 → Content 저장 핸들러 진입.
 * - "Lot라벨자동출력"이 Y이면, API 호출 전에 extractUnprintedLabelData(store, deletedLotList) 호출해서
 *   삭제 대상 제외·아직 printed 아님·입고량>0·inserted 인 LOT만 LabelPrintData[] 로 추출해 unprintedLabelData에 담아 둠.
 * - 그 다음 post/patch API 호출 → 저장 성공 시 (삭제가 아닐 때만) printBulkLabel(unprintedLabelData, 'label1') 호출.
 *
 * 흐름 2) 검색 영역 수동 출력
 * - 사용자가 품목별 LOT 그리드에서 LOT를 선택한 뒤 "라벨 출력" 버튼 클릭.
 * - SearchSection에서 selectedLabelData를 만듦: 선택된 valueIndex마다 buildLabelDataByValueIndex(valueIndex, itemCd, store) 호출해서
 *   LabelPrintData[] 를 만든 뒤, 출력 버튼 클릭 시 printBulkLabel(selectedLabelData, 'label1') 호출. 성공 시 해당 LOT들 printed: true 로 갱신.
 *
 * buildLabelData는 "LOT 1건 → LabelPrintData 1건" 변환용. extractUnprintedLabelData와 buildLabelDataByValueIndex 내부에서 호출됨.
 */
import { LabelPrintData } from "src/apis/labelApi";
import { formatDate } from "src/utils/string/stringUtil";

/** buildLabelData / extractUnprintedLabelData / buildLabelDataByValueIndex 호출 시 넘기는 스토어 스냅샷 */
export interface WMS1022_R01StoreData {
    WMS1022_R01LotMap: Map<string, any[]>;
    WMS1022_R01DetailList: any[];
    editWMS1022_R01: any;
}

/** 수량 포맷 (콤마 + 단위) */
const formatQty = (qty: number | undefined, unit: string): string => {
    if (!qty && qty !== 0) return '';
    return `${qty.toLocaleString()}${unit || 'EA'}`;
};

/** LOT 1건 → LabelPrintData 1건 변환. extractUnprintedLabelData·buildLabelDataByValueIndex가 이 함수를 호출해서 배열을 만든다. */
export const WMS1022_R01_buildLabelData = (
    lotNo: string,
    itemCd: string,
    store: WMS1022_R01StoreData
): LabelPrintData => {
    const { WMS1022_R01LotMap, WMS1022_R01DetailList, editWMS1022_R01 } = store;

    const lotList = WMS1022_R01LotMap.get(itemCd) || [];
    const lot = lotList.find((l: any) => l.lotNo === lotNo);
    const detail = WMS1022_R01DetailList.find((d: any) => d.itemCd === itemCd);
    const header = editWMS1022_R01;

    if (!lot) throw new Error(`LOT를 찾을 수 없습니다: ${lotNo}`);
    if (!detail) throw new Error(`품목 정보를 찾을 수 없습니다: ${itemCd}`);
    if (!header) throw new Error('입고 정보를 찾을 수 없습니다');

    const transportDate = header.orderDt
        ? formatDate(new Date(header.orderDt))
        : formatDate(new Date());

    // WMS1022는 구매입고 → 항상 [자재입고], 위치는 품목 locationNm
    return {
        lotNo: lot.lotNo || '',
        itemCd: detail.itemCd || '',
        itemNm: detail.itemNm || '',
        itemSpec: detail.itemSpec || '',
        transportDate,
        itemAccountNm: detail.itemAccountNm || header.inoutMethod || '',
        storageLocation: detail.locationNm || detail.locationCd || '',
        qty: formatQty(lot.lotQty, detail.orderUnit || 'EA'),
        customer: header.customNm || '',
        customerItemCd: detail.customerItemCd || detail.itemCd || '',
        labelType: '[자재입고]',
    };
};

/**
 * valueIndex로 LOT를 찾아 buildLabelData 호출. SearchSection의 selectedLabelData를 만들 때, 선택된 LOT(valueIndex)마다 이 함수를 호출해
 * LabelPrintData 1건씩 모아 배열로 둔 뒤, "라벨 출력" 버튼 클릭 시 그 배열을 printBulkLabel에 넘긴다.
 */
export const WMS1022_R01_buildLabelDataByValueIndex = (
    valueIndex: number,
    itemCd: string,
    store: WMS1022_R01StoreData
): LabelPrintData | null => {
    const { WMS1022_R01LotMap } = store;
    const lotList = WMS1022_R01LotMap.get(itemCd) || [];
    const lot = lotList.find((l: any) => l.valueIndex === valueIndex);

    if (!lot) {
        console.warn(`LOT를 찾을 수 없습니다: valueIndex=${valueIndex}, itemCd=${itemCd}`);
        return null;
    }

    try {
        return WMS1022_R01_buildLabelData(lot.lotNo, itemCd, store);
    } catch (error) {
        console.error('라벨 데이터 생성 실패:', error);
        return null;
    }
};

/**
 * 저장 핸들러에서 호출. "Lot라벨자동출력"=Y일 때만 사용.
 * 저장 API 호출 전에, 삭제 대상 제외·printed 아님·입고량>0·inserted 인 LOT만 골라 buildLabelData로 변환한 배열을 반환하고,
 * Content에서는 이 배열을 unprintedLabelData에 담아 두었다가 저장 성공 후 printBulkLabel(unprintedLabelData, 'label1') 호출.
 */
export const WMS1022_R01_extractUnprintedLabelData = (
    store: WMS1022_R01StoreData,
    deletedLotList: string[] = []
): LabelPrintData[] => {
    const { WMS1022_R01LotMap } = store;
    const unprintedLabelData: LabelPrintData[] = [];
    const deletedSet = new Set(deletedLotList);

    WMS1022_R01LotMap.forEach((lotList, itemCd) => {
        lotList.forEach((lot: any) => {
            const valueIndex = lot.valueIndex?.toString() || '';
            if (
                !deletedSet.has(valueIndex) &&
                !lot.printed &&
                lot.lotQty > 0 &&
                lot.inserted === true
            ) {
                try {
                    unprintedLabelData.push(WMS1022_R01_buildLabelData(lot.lotNo, itemCd, store));
                } catch (error) {
                    console.error(`라벨 데이터 구성 실패: ${lot.lotNo}`, error);
                }
            }
        });
    });

    return unprintedLabelData;
};
